import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn } from '../ui/cn';
import {
  buildDiagnostics,
  environmentLabel,
  isProduction,
  versionSummary,
} from '../../lib/appVersion';
import { wikiUrl } from '../../lib/wikiUrl';

/**
 * Application footer / `contentinfo` landmark (internal-tool "what am I running /
 * where do I get help" strip). Presentation-only, token-styled, no PII (SEC-8).
 * Neutral brand posture (ADR-015) with a scoped carve-out for the Aurora values
 * banner at the bottom of this footer only (amendment 2026-07-10). Renders
 * ownership + injected build metadata (see `lib/appVersion.ts`), a non-production
 * environment badge, a set of config-gated links (dead links are never shown), a
 * copy-build-info control for support tickets, and the values strip. Must sit
 * OUTSIDE `<main>` so the landmark is unique.
 */

// Wiki registry slugs — flagged to match the TAG Operations wiki taxonomy (PRO-0480).
const HELP_SLUG = 'help';
const ACCESSIBILITY_SLUG = 'accessibility';
const PRIVACY_SLUG = 'privacy';

/** Official Aurora values banner (ADR-015 amendment 2026-07-10 — footer-only). */
const VALUES_BANNER_SRC = '/aurora-values-banner.png';
const VALUES_BANNER_ALT =
  'Aurora values: Caring, Ambitious, Collaborative, Trusted, Innovative';

interface FooterLink {
  readonly label: string;
  readonly href: string;
  /** External (wiki/service-desk) links open in a new tab; mailto stays in-page. */
  readonly external: boolean;
}

function resolveLinks(): FooterLink[] {
  const links: FooterLink[] = [];

  const help = wikiUrl(HELP_SLUG);
  if (help) {
    links.push({ label: 'Help', href: help, external: true });
  }

  const supportUrl = import.meta.env.VITE_SUPPORT_URL?.trim();
  const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL?.trim();
  if (supportUrl) {
    links.push({ label: 'Report an issue', href: supportUrl, external: true });
  } else if (supportEmail) {
    links.push({ label: 'Report an issue', href: `mailto:${supportEmail}`, external: false });
  }

  const accessibility = wikiUrl(ACCESSIBILITY_SLUG);
  if (accessibility) {
    links.push({ label: 'Accessibility', href: accessibility, external: true });
  }

  const privacy = wikiUrl(PRIVACY_SLUG);
  if (privacy) {
    links.push({ label: 'Privacy', href: privacy, external: true });
  }

  return links;
}

export function AppFooter() {
  const [copied, setCopied] = useState(false);
  const year = new Date().getFullYear();
  const links = resolveLinks();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildDiagnostics());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <footer className="border-t border-border text-xs text-text-secondary">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-4">
        <span>
          TAG Operations &middot; &copy; {year} The Aurora Group
        </span>

        <span aria-hidden className="text-border">
          |
        </span>

        <span className="font-mono" title={`Build ${versionSummary()}`}>
          {versionSummary()}
        </span>

        {!isProduction && (
          <span
            className={cn(
              'inline-flex items-center rounded-sm bg-warning-bg px-2 py-0.5 font-medium text-warning-foreground',
            )}
          >
            {environmentLabel()}
          </span>
        )}

        {links.length > 0 && (
          <nav aria-label="Footer" className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {links.map((link) => (
              <a
                key={link.label}
                href={link.href}
                {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className="rounded-sm underline underline-offset-2 hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                {link.label}
              </a>
            ))}
          </nav>
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="ml-auto min-h-11 text-xs font-medium"
        >
          {copied ? (
            <>
              <Check aria-hidden className="h-4 w-4" />
              Copied
            </>
          ) : (
            <>
              <Copy aria-hidden className="h-4 w-4" />
              Copy build info
            </>
          )}
        </Button>
      </div>

      <img
        src={VALUES_BANNER_SRC}
        alt={VALUES_BANNER_ALT}
        className="block w-full h-auto"
      />
    </footer>
  );
}
